import React from 'react';
import { Link } from 'react-router-dom';
import { products, findProduct, productUrl } from '../../content/products';
import { elokHub, aqaraHub } from '../../content/shopContent';
import { ProductCard, Breadcrumbs, Html, PageHero, Section } from '../../components/shop/ShopBits';
import LockWizard from '../../components/shop/LockWizard';
import usePageMeta from '../../hooks/usePageMeta';

// One page shape for every brand we lead with: the door-first chooser filtered to
// the brand, a "by door type" table, the range, and the brand's own sections.
const BRANDS = {
  'e-lok': { name: 'E-LOK', content: elokHub, sub: 'Answer two or three questions and we point you at the right series. Every E-LOK keeps a key override and works without a hub.', range: (p) => p.brand === 'E-LOK' && p.fit.doorMaterials.length > 0 },
  aqara: { name: 'Aqara', content: aqaraHub, sub: 'Answer two questions and we point you at the right lock. Every Aqara lock keeps a key override, and we can set up the hub and your phone on the day.', range: (p) => p.brand === 'Aqara' && p.fit.doorMaterials.length > 0 },
};

const BrandHub = ({ brand }) => {
  usePageMeta();
  const b = BRANDS[brand];
  const c = b.content;
  return (
    <div className="min-h-screen bg-white">
      <PageHero title={c.h1} sub={b.sub} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: b.name }]} />
        <div className="grid lg:grid-cols-2 gap-10">
          <LockWizard brand={b.name} testId={`${brand}-chooser`} compact />
          <div>
            <Html html={c.intro} />
            <h2 className="text-2xl font-bold text-gray-900 mt-6 mb-3">Which one, by door</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-2 pr-4">Door</th>
                    <th className="py-2 pr-4">Pick</th>
                    <th className="py-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {c.chooser.map((row) => (
                    <tr key={row.when} className="border-t border-gray-200 align-top">
                      <td className="py-2 pr-4 text-gray-700">{row.when}</td>
                      <td className="py-2 pr-4">
                        <Link to={productUrl(findProduct(row.slug))} className="text-red-600 font-semibold">
                          {row.pick}
                        </Link>
                      </td>
                      <td className="py-2 text-gray-600">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              Not sure it is the right brand for your door? <Link to="/shop/find-my-lock" className="text-red-600 font-semibold">Find the lock for my door</Link> compares every brand we carry.
            </p>
          </div>
        </div>
      </div>
      <Section title={`The ${b.name} range`} className="bg-gray-50">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.filter(b.range).map((p) => (
            <ProductCard key={p.slug} product={p} />
          ))}
        </div>
      </Section>
      <Section>
        <div className="max-w-3xl">
          {c.sections.map((s) => (
            <div key={s.h2}>
              <h2 className="text-2xl font-bold text-gray-900 mt-2 mb-3">{s.h2}</h2>
              <Html html={s.html} />
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default BrandHub;
